import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  inject,
  Optional,
  signal,
  Signal,
  OnInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CreateModuleRightsDirective } from '../../../core/auth/directives/can-create-module.directive';
import { DeleteModuleRightsDirective } from '../../../core/auth/directives/can-delete-module.directive';
import { TableComponent } from '../table.component';
import { EditModuleRightsDirective } from '../../../core/auth/directives/can-edit-module.directive';
import { ModalService } from '../../services/modal.service';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { TitleService } from '../../services/title.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { UserPreferenceStore } from '../../../core/services/user-preference/user-preference.store';
import { searchFieldVisible } from '../../utils/search-visibility.util';
import { ViewEditModuleDirective } from '../../../core/auth/directives/view-or-edit-module.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { catchError, EMPTY, of, tap, takeUntil } from 'rxjs';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { UserStore } from 'app/core/services/user.store';
import { cloneDeep } from 'lodash-es';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InterfaceAction } from '../../../features/admin/modules/features/interface-actions/interface-actions.model';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import { DetailsModalTemplateComponent } from '../../../features/admin/modules/modules-entity/templates/details/details-modal/details-modal.component';
import { Sort } from '@angular/material/sort';
import {
  aanmeldingDocumentsPendingValidator,
  type LinkedDocument,
} from '../../validators/aanmelding-documents-pending.validator';
import { debugLog } from '../../utils/debug-logger';
import { classifyEntriesLoadError } from '../../utils/entries-load-error';
import type { EntriesLoadError } from '../../utils/entries-load-error.types';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { clearableIsReadOnly } from './clearable-field.type';
import type { TableColumn } from '../../interfaces/table.models';
import { EntriesLoadErrorAlertComponent } from '../entries-load-error-alert/entries-load-error-alert.component';
import { canMutateField } from './readonly/readonly.types';

// Module-code, not UUID — keeps the badge wired correctly across environments
// where module IDs differ. Generalize to a prop (e.g. props.trackPendingDocs)
// when a second module needs the same pending-counter pattern.
const DOCUMENTEN_MODULE_CODE = 'documents';

/**
 * Read-only linked-items (#20955): columns without linkType so title cells render as plain text.
 *
 * These columns are handed to the table as `defaultColumns`, and `resolveLiveTableColumn` reads
 * `linkType` from THAT list rather than from a user's saved preference — so the stripping survives
 * the preference merge no matter what the column editor starts persisting.
 *
 * The second, independent guard is that this component passes no `moduleSlug`: the table's default
 * title anchor is only offered where it knows the module route, so a read-only linked-items table
 * stays inert even for a column that somehow kept `linkType: 'detail'`.
 */
export function stripColumnLinks(columns: TableColumn[]): TableColumn[] {
  return columns.map(({ linkType: _linkType, ...column }) => column as TableColumn);
}

class DialogData {}

@Component({
  selector: 'formly-linked-items',
  template: `
    <!-- No own card chrome or title (#20813): the surrounding pages-card already names the
         section — an own h3 duplicated it and the fuse-card outline nested a card in a card.
         Pre-#20813 anatomy, current styling: search field with the add-button on its right. -->
    <div class="flex flex-col gap-4 min-w-0">
      <!-- Opt-in section heading (#20952): the Figma edit-article page names the block above the
           table ("Prijsafspraken voor dit artikel"). Unset keeps the #20813 behavior of no title,
           so pages whose card already names the section do not get a second heading. -->
      @if (sectionTitle()) {
        <h3 class="dd-linked-items__section-title">{{ sectionTitle() }}</h3>
      }
      <!-- "Save first" only matters when adding is possible at all — read-only mode never adds. -->
      @if (!readOnlyView() && formState.entry?.id === undefined) {
        <fuse-alert class="clear" [type]="'warning'"
          >Je kan pas {{ props.label }} toevoegen als je huidige item hebt opgeslagen.</fuse-alert
        >
      }
      <!-- A failed load must not render as a plain empty table: "there is nothing here" and
           "you may not see what is here" are different answers, and the second one used to get
           the look of the first while the error escaped into window.onerror. -->
      @if (loadError()) {
        <app-entries-load-error-alert
          class="clear"
          [error]="loadError()!"
          [label]="props.label"
          dataCyPrefix="linked-items"
          [showRetry]="true"
          (retry)="getEntries()" />
      }
      <!-- Search stays available in read-only mode (lists load up to 1000 entries without
           pagination); only the add-button is an editing affordance. The field is
           admin-configurable off via the showSearch prop AND only appears above SEARCH_MIN_ROWS
           rows (search-visibility.util.ts — the rule widgets follow), and is hidden on
           'forbidden': searching a list you may not read is meaningless. The add-button is
           governed solely by *canCreateModule: create-without-read is a valid combination. -->
      @if (searchVisible() || !readOnlyView()) {
        <div class="flex items-center justify-end gap-4">
          @if (searchVisible() && loadError() !== 'forbidden') {
            <mat-form-field class="dd-search-field fuse-mat-dense hide-subscript-wrapper flex flex-1 min-w-50 pb-0">
              <mat-icon matPrefix class="icon-size-5" [svgIcon]="'heroicons_solid:magnifying-glass'"></mat-icon>
              <input
                #searchInput
                matInput
                type="search"
                [placeholder]="'Zoeken in ' + props.label.toLowerCase()"
                [(ngModel)]="search" />
              <div matSuffix class="dd-clear-suffix">
                <app-clear-field-button
                  [hasValue]="!!search()"
                  [ariaLabel]="'Zoekopdracht wissen'"
                  [focusTarget]="searchInput"
                  (clearSelection)="search.set('')" />
              </div>
            </mat-form-field>
          }
          @if (!readOnlyView()) {
            <button
              *canCreateModule="{ moduleId: linkedModule()?.id, show: true }"
              type="button"
              class="flex flex-0 dd-secondary-button dd-button-sm"
              mat-flat-button
              [disabled]="formState.entry?.id === undefined || !canMutate()"
              data-cy="linked-items_add-button"
              (click)="showModal(undefined, true)">
              <mat-icon class="icon-size-5" [svgIcon]="'heroicons_solid:plus'"></mat-icon
              ><span class="hidden sm:inline-block"
                >&nbsp;
                {{ props.addText ? props.addText : props.label + ' toevoegen' }}
              </span>
            </button>
          }
        </div>
      }
      <!-- [module] lets badge/status columns resolve the module's statusBadgeMapping; the
           linkedField column is hidden — inside this section it is by definition the parent. -->
      @if (loadError() !== 'forbidden') {
        <app-table
          class="flex-1 w-full min-w-0"
          [list]="visibleEntries()"
          [searchTerm]="search()"
          [showbulkActions]="canMutate()"
          [defaultColumns]="columns()"
          [tableId]="linkedModule()?.id"
          [moduleId]="linkedModule()?.id"
          [moduleSlug]="rowLinkSlug()"
          [module]="linkedModule()"
          [hiddenColumnIds]="hiddenLinkedColumnIds()"
          [compactSettingsButton]="true"
          [hideDefaultActions]="readOnlyView()"
          [readOnly]="readOnlyView()"
          (bulkActionClick)="processBulkAction($event)"
          (sortChange)="getEntriesWithNewSort($event)">
          <ng-template #actionsTemplate let-columnInformation="column" let-entry="entry">
            @if (!readOnlyView()) {
              <div class="flex items-center gap-1">
                <ng-container *viewEditModule="{ moduleId: linkedModule()?.id }; viewTpl: viewButton; editTpl: editButton">
              </ng-container>

              <ng-template #viewButton>
                <button
                  type="button"
                  mat-icon-button
                  *canViewModule="{ moduleId: linkedModule()?.id, show: true }"
                  class="text-accent hover:text-primary transition-colors"
                  [matTooltip]="'Bekijken'"
                  data-cy="linked-items_edit-button"
                  (click)="showModal(entry, false)">
                  <mat-icon [ngClass]="'text-inherit'">eye</mat-icon>
                </button>
              </ng-template>

              <ng-template #editButton>
                <button
                  type="button"
                  mat-icon-button
                  *canEditModule="{ moduleId: linkedModule()?.id, show: true }"
                  class="text-accent hover:text-primary transition-colors"
                  [matTooltip]="'Bewerken'"
                  [disabled]="!canMutate()"
                  data-cy="linked-items_edit-button"
                  (click)="showModal(entry, true)">
                  <mat-icon [ngClass]="'text-inherit'">edit</mat-icon>
                </button>
              </ng-template>

              <button
                *canDeleteModule="{ moduleId: linkedModule()?.id, show: true }"
                mat-icon-button
                type="button"
                class="text-hint hover:text-warn-800 transition-colors"
                [matTooltip]="'Verwijderen'"
                [disabled]="!canMutate()"
                data-cy="linked-items_delete-button"
                (click)="deleteConformation(entry.id)">
                <mat-icon class="icon-size-4.5" [ngClass]="'text-inherit'">delete</mat-icon>
              </button>
            </div>
            }
          </ng-template>
        </app-table>
      }
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatInputModule,
    ReactiveFormsModule,
    TableComponent,
    FormsModule,
    NgClass,
    CreateModuleRightsDirective,
    DeleteModuleRightsDirective,
    ViewEditModuleDirective,
    MatTooltip,
    FuseAlertComponent,
    EditModuleRightsDirective,
    ClearFieldButtonComponent,
    EntriesLoadErrorAlertComponent,
  ],
  styles: `
    /* Figma renders the section heading in exactly the field-label style of the same form —
       compare the Categorie label above it. The values mirror .dd-tags-chips-field__label in
       tags.component.ts (Figma 14px Medium, Foreground/Default/Primary via --dd-field-label);
       an h3 keeps the heading semantics without borrowing heading typography. */
    .dd-linked-items__section-title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.4;
      color: var(--dd-field-label);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyLinkedItemsComponent extends FieldType implements OnInit {
  modulesStore = inject(ModulesStore);
  userPreferenceStore = inject(UserPreferenceStore);
  utilityService = inject(UtilityService);
  entriesService = inject(EntriesService);
  modalService = inject(ModalService);
  formService = inject(FormService);
  titleService = inject(TitleService);
  userStore = inject(UserStore);
  _fuseConfirmationService = inject(FuseConfirmationService);
  private destroy$ = injectDestroy();
  /** Client-side table filter. A signal because `searchVisible()` reads it (see that computed). */
  readonly search = signal('');

  route = inject(ActivatedRoute);
  // Default keeps currentModule()'s paramsParent() call safe when there is no route.parent (a
  // dialog or Storybook context) — the constructor below only reassigns it when one exists.
  paramsParent: Signal<ParamMap | undefined> = signal(undefined);
  params = toSignal(this.route.paramMap);

  currentModule = computed(() => {
    if (this.paramsParent() === undefined) {
      // MAT_DIALOG_DATA is @Optional(): this field also renders outside a dialog (no
      // route.parent AND no dialog data — e.g. a Storybook shadow), where `data` is null.
      return this.data?.['entity'];
    }
    // TODO MAKE THIS MORE DYNAMIC
    if (this.paramsParent()?.get('name') === 'bedrijfsinstellingen') {
      // Special case for bedrijfsinstellingen, where the module slug is 'companies'
      return this.modulesStore.enabledModules().find(module => module.slug === 'companies');
    }
    if (this.paramsParent()?.get('name') === 'profiel') {
      // Special case for bedrijfsinstellingen, where the module slug is 'users'
      return this.modulesStore.enabledModules().find(module => module.slug === 'users');
    }

    return this.modulesStore.enabledModules().find(module => module.slug === this.paramsParent()?.get('name'));
  });
  currentEntry = computed(() => {
    if (this.params()?.get('id') === undefined) {
      return this.data?.['model'];
    }
    if (this.options.formState?.entry != null) {
      return this.options.formState.entry;
    }

    return this.modulesStore.entries()[this.currentModule()?.id]?.find(entry => {
      return entry.id === this.params()?.get('id');
    });
  });
  linkedModule = computed(() => {
    return this.modulesStore.enabledModules().find(module => module.code === this.props.linkedModule);
  });
  linkedEntryId = computed(() => {
    if (this.options.formState?.entry != null) {
      // fix for /profiel and /bedrijfsinstellingen
      return this.options.formState?.entry.id;
    } else if (this.titleService.getTitle().toLowerCase() === 'profiel') {
      return this.userStore.currentUserId();
    } else if (this.data != undefined && this.data['model']?.id) {
      return this.data['model']?.id;
    } else {
      return this.params()?.get('id');
    }
  });

  entries = signal<Entry[]>([]);
  activeSort = signal<Sort | undefined>(undefined);

  /** #20955: read-only rendering — no add/search-to-add UI, no row actions, plain-text titles. */
  readonly readOnlyItems = computed(() => this.props['readOnlyItems'] === true);

  /**
   * No mutation affordances at all: the admin-configured read-only list (#20955) OR the whole
   * page in read mode (#21239, task 21244).
   *
   * The two modes share everything EXCEPT the links (see `columns()`), which is why this is a
   * separate computed rather than a widened `readOnlyItems`. Page read mode is a page being
   * READ, so its rows must stay reachable; `readOnlyItems` presents the list as flat reference
   * data and strips them.
   *
   * `canMutate()` already refuses every mutation for `props.readonly` — this is what stops the
   * portal from OFFERING one it will then refuse.
   */
  readonly readOnlyView = computed(() => this.readOnlyItems() || clearableIsReadOnly(this.props));

  /**
   * Slug the table builds its row/title links from — set ONLY in page read mode.
   *
   * Without a slug the table falls back to `/admin/<id>` (table.component.ts:418, :891), which
   * is not a module route: `/:name/:id` resolves `name` as a module slug (app.routes.ts:203).
   * So a linked list whose title column is configured with `linkType: 'detail'` has always
   * rendered an anchor that goes nowhere — and read mode's promise is precisely that those rows
   * DO click through, so it has to be given the real slug.
   *
   * Deliberately not set in edit mode, even though the same broken anchor is there: handing the
   * table a slug also switches on WHOLE-ROW navigation (`rowLinkEnabled`), and in edit mode a
   * row click is the edit modal's, not the router's. Fixing the edit-mode anchor is a separate
   * change with its own decision to make about that row click.
   *
   * Also not set for `readOnlyItems`: a slug alone now switches on the default title anchor for any
   * column without `linkType`, and `stripColumnLinks` produces exactly such columns — so passing
   * it there would turn the admin's flat reference list back into navigable rows.
   */
  readonly rowLinkSlug = computed(() =>
    this.readOnlyView() && !this.readOnlyItems() ? this.linkedModule()?.slug : undefined
  );

  /**
   * Admin opt-out (`props.showSearch === false`) over the shared row-count rule: on a short list
   * a search field is noise, so it appears only above SEARCH_MIN_ROWS — the same rule widgets
   * follow. `entries()` is an honest total here (`getEntries` asks for `limit: 1000` without
   * pagination and the term filters client-side in the table), so this needs none of the
   * page-size proxy guarding `WidgetDataCountService` has to do.
   *
   * Deliberately `this.entries()`, never `this.visibleEntries()`: the threshold is about how long
   * the loaded list IS, not how much of it survives the active term — that distinction is what
   * keeps the field from vanishing out from under a zero-result search (see `hasTerm` above and
   * `filters the list on the active term` in the spec). Swapping this to the filtered list would
   * make the field disappear exactly when the user most needs it to correct their query.
   */
  readonly searchVisible = computed(() =>
    searchFieldVisible({
      rowCount: this.entries().length,
      hasTerm: this.search().length > 0,
      optedOut: this.props['showSearch'] === false,
    })
  );

  /**
   * Client-side filter over the loaded list, restoring what commit e7200c3a (2025-02-11, "Create
   * search with QueryParams") removed for this consumer: that commit moved the module list to
   * server-side search via query params and dropped `app-table`'s own `dataSource.filter =
   * search?.toLowerCase()` assignment. Consumers with server-side search were unaffected, but
   * linked-items loads its list once (`limit: 1000`) and never re-queries on the term, so the
   * search field kept rendering while typing into it silently did nothing.
   *
   * Not fixed in `TableComponent` itself: re-adding the filter there would double-filter every
   * page that already searches server-side, and could drop rows the server matched on a field the
   * table does not render.
   *
   * Same matching rule as the table's own predicate (`UtilityService.setFilterPredicate`:
   * `JSON.stringify(data).toLowerCase().indexOf(filter)`), so filtering here behaves exactly like
   * the client-side filtering `dataSource.filterPredicate` still wires up for everyone else — the
   * term is lowercased before matching, same as the removed table line did.
   *
   * The haystacks are built once per `entries()` change (`#searchHaystacks`) rather than inside
   * the filter: the search input is bound without a debounce, so calling the predicate directly
   * would serialise every loaded entry — up to the 1000-row cap — on every keystroke.
   */
  readonly #searchHaystacks = computed(() =>
    this.entries().map(entry => ({ entry, text: JSON.stringify(entry).toLowerCase() }))
  );

  readonly visibleEntries = computed(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.entries();
    return this.#searchHaystacks()
      .filter(({ text }) => text.includes(term))
      .map(({ entry }) => entry);
  });

  /** Optional section heading above the list (#20952); empty/unset renders no title at all. */
  readonly sectionTitle = computed(() => (this.props['sectionTitle'] as string | undefined)?.trim() || '');

  /** The column holding the parent link is redundant inside its own linked-items section. */
  readonly hiddenLinkedColumnIds = computed(() => (this.props.linkedField ? [this.props.linkedField] : []));
  /**
   * Why the last load failed, if it did. `null` means `entries()` is real data.
   *
   * 'forbidden' is its own state because a 403 here is normal: the linked module is authored on
   * the form for every role, so a role without read permission on it is a configuration outcome,
   * not an incident. It must read as "you may not see this", never as an empty list.
   */
  loadError = signal<EntriesLoadError | null>(null);

  /**
   * Monotonic request counter. `getEntries()` is re-entered from `ngOnInit`, sort changes, the
   * modal-save flow, and `processBulkAction`'s delayed reload, with no cancellation of the
   * in-flight request. Each call captures its own sequence number so a stale response — one that
   * resolves after a newer call already started — cannot overwrite `loadError`/`entries` with
   * outdated state.
   */
  #loadSeq = 0;

  columns = computed(() => {
    if (this.linkedModule()?.id === undefined || this.userPreferenceStore?.columns() == null) {
      return [];
    }
    const sourceColumns = this.userPreferenceStore?.columns()?.[this.linkedModule()?.id] ?? [];
    // Deliberately `readOnlyItems()`, not `readOnlyView()`: page read mode KEEPS the links —
    // clicking through to the linked entry is the reader's next step, and dropping the anchors
    // would turn the section into a dead end. See `readOnlyView` above.
    return this.readOnlyItems() ? stripColumnLinks(sourceColumns) : sourceColumns;
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    super();

    if (this.route?.parent?.paramMap) {
      this.paramsParent = toSignal(this.route?.parent?.paramMap);
    }
  }

  ngOnInit() {
    this.getEntries();
  }

  getEntriesWithNewSort(sort: Sort) {
    this.activeSort.set(sort);
    this.getEntries();
  }

  getEntries() {
    // key = 'entryId' is used for the 'documents' which is a special case. Instead of entry object it only contains the entry id
    const linkedField =
      this.props.linkedField?.includes('entryId') || this.props.linkedField?.includes('-id')
        ? this.props.linkedField
        : this.props.linkedField + '.id';

    // check if linkedField is in an array. Falls back to [] when no module is linked (or its
    // generated form hasn't loaded yet): FormHelper.fieldIsInArray iterates this array
    // unconditionally (unlike its getFields/getFieldByKeyPath siblings, which already guard
    // `!generatedForm`), so an undefined value here throws "fieldsArray is not iterable" before
    // linkedModule() even reaches the template guards below.
    const generatedForm = this.modulesStore.generatedForms()[this.linkedModule()?.id] ?? [];
    const field = FormHelper.getFieldByKeyPath(this.props.linkedField, generatedForm);
    const isInArray = FormHelper.fieldIsInArray(field, generatedForm);
    let filter: string = `${UtilityService.dotPathToObjectPath(linkedField)} eq '${this.linkedEntryId()}'`;

    if (isInArray) {
      filter = `smartPath(c, '${this.props.linkedField}.id', ['${this.linkedEntryId()}'])`;
    }

    if (this.linkedModule()?.id) {
      // Captured before the request fires: identifies this call among any others that overlap
      // it, so a stale response can recognize itself as stale (see #loadSeq).
      const seq = ++this.#loadSeq;
      // loadError is deliberately NOT cleared here. Clearing it up-front made a retry (and every
      // other re-entry: sort change, modal save, bulk action) drop 'forbidden' before the new
      // answer arrived, so the table — gated on exactly that state — re-rendered for the length of
      // the request. Only an actual outcome changes the state now: the subscribe clears it, the
      // catchError replaces it.
      // use entriesService.service so global state will not be updated
      this.entriesService
        .getEntries({
          moduleId: this.linkedModule().id,
          offset: 0,
          limit: 1000,
          filter: filter,
          orderBy: this.activeSort() ? this.activeSort() : undefined,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            debugLog('LinkedItems', 'entries load failed', {
              linkedModule: this.props.linkedModule,
              status: error.status,
            });
            // A newer request already superseded this one — its outcome, not this failure, is
            // what should reach loadError/entries.
            if (seq !== this.#loadSeq) return EMPTY;
            const classified = classifyEntriesLoadError(error);
            this.loadError.set(classified);
            // A 403 means the user can no longer see these rows at all, so the rows from the last
            // successful load — and any pending-docs count derived from them — are stale and must
            // go. Dropping them keeps 'forbidden' meaning one thing everywhere: there is no data
            // here for this user, in state as well as on screen. The count goes through the same
            // notify path (with an empty list) so it applies the same guards (documenten-module
            // check, detailsService presence) as a normal load.
            if (classified === 'forbidden') {
              this.entries.set([]);
              this.#notifyPendingDocsCount([]);
            }
            // No fallback value emitted: a *transient* failure must leave previously loaded rows
            // (and the pending-docs banner they drive) untouched instead of wiping them to an
            // empty list — the table stays usable, the alert explains that the reload failed.
            return EMPTY;
          })
        )
        .subscribe(entries => {
          if (seq !== this.#loadSeq) return;
          // A successful response is the only thing that lifts a previous error: until it lands,
          // the alert keeps standing and the content stays gated.
          this.loadError.set(null);
          this.entries.set(entries);
          this.#notifyPendingDocsCount(entries);
        });
    }
  }

  /**
   * When the linked module is the documenten module, compute how many entries
   * are still pending (no file or not approved) and notify the parent
   * DetailsService so it can surface the warning banner and tab counter.
   */
  #notifyPendingDocsCount(entries: Entry[]): void {
    if (this.linkedModule()?.code !== DOCUMENTEN_MODULE_CODE) return;

    const detailsService = this.options?.formState?.detailsService;
    if (!detailsService) return;

    // documenten.entry.status — single option-object {slug,...} (custom-select / taxonomy).
    // documenten.entry.file — uploaded document-reference object {documentId, documentName, ...}.
    const docs: LinkedDocument[] = entries.map(e => ({
      status: e.entry?.['status'] as { slug?: string } | null | undefined,
      file: e.entry?.['file'] as LinkedDocument['file'],
    }));

    const result = aanmeldingDocumentsPendingValidator(docs);
    const count = result?.pendingDocuments.count ?? 0;

    debugLog('OnboardingDocs', `linked-items: ${entries.length} documenten, ${count} pending`);
    detailsService.updatePendingDocsCount(count);
  }

  prefillLinkedFormValues(entry: Entry | undefined): Entry {
    if (entry) {
      return entry;
    }

    let newEntry: Entry = {
      id: undefined,
      entry: {
        archived: false,
      },
    };

    // Handle the normal linked field first
    FormHelper.getFields(this.modulesStore.generatedForms()[this.linkedModule()?.id]).map((field: any) => {
      let currentEntry: Entry;

      if (this.currentEntry() != undefined) {
        currentEntry = cloneDeep(this.currentEntry());
        delete currentEntry._etag;
      }

      const lastPartOfKeyPath = this.props.linkedField.split('.').pop();

      if (field.key === lastPartOfKeyPath) {
        if (field.props.optionsArray != undefined) {
          currentEntry.selectedInput = UtilityService.keyPathToValue(
            currentEntry,
            field.props.optionsArray[0].moduleLinkedField
          );
        }
        newEntry = UtilityService.setKeyValuePath(newEntry, field.props.keyPath, currentEntry);
      }
    });

    // Handle defaultValuesLinkedItems
    if (this.field.props.defaultValuesLinkedItems) {
      newEntry = this.formService.fillEntryWithMappingItems(
        newEntry,
        this.field.props.defaultValuesLinkedItems,
        this.currentModule()?.id,
        this.currentEntry()
      );
    }

    return newEntry;
  }

  showModal(entry: Entry | undefined, mutate: boolean) {
    if (mutate && !this.canMutate()) return;
    const modal = this.modalService.openEntryInModal(
      this.linkedModule(),
      this.prefillLinkedFormValues(entry),
      this.props.label,
      false
    );
    modal
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result || !mutate || !this.canMutate()) return;

        const saveEntry =
          entry?.id != null
            ? this.entriesService.editEntry(this.linkedModule().id, entry.id, result.entry, result.files)
            : this.entriesService.addEntry(this.linkedModule().id, result.entry.entry, result.files);

        saveEntry
          .pipe(
            tap(() => {
              this.getEntries();
              this.utilityService.notify('Item ' + (entry?.id != null ? 'bewerkt' : 'toegevoegd'));
            }),
            catchError(e => {
              console.error(e);
              const errors = e.error.errors.map(
                (err: { errorMessage: string; propertyName: string }) => err.errorMessage
              );
              result.entry.status.errors.push(errors);
              this.utilityService.notify(errors.join('\n'));

              this.showModal(result.entry, true);
              this.getEntries();
              return of(null);
            })
          )
          .subscribe();
      });
  }

  processBulkAction(action: { name: string; entries: Entry[]; interfaceAction?: InterfaceAction }) {
    if (!this.canMutate()) return;
    this.entriesService.processBulkAction(action, this.linkedModule()).subscribe(() => {
      setTimeout(() => {
        this.getEntries();
      }, 1000); // Delay to ensure the UI updates after the action
    });
  }

  deleteConformation(id: string) {
    if (!this.canMutate()) return;
    const confirmation = this._fuseConfirmationService.open({
      title: this.props.label + ' verwijderen',
      message: 'Weet je zeker dat je het ' + this.props.label + ' wil verwijderen?',
      actions: {
        cancel: {
          label: 'Annuleren',
        },
        confirm: {
          label: 'Verwijderen',
        },
      },
    });

    confirmation
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result === 'confirmed' && this.canMutate()) {
          this.modulesStore.deleteEntry({
            moduleId: this.linkedModule()?.id,
            entryId: id,
          });

          this.entries.set(UtilityService.deleteIdFromArray(this.entries(), id));
        }
      });
  }

  canMutate(): boolean {
    // `readOnlyItems()` is component-specific (a per-item override), not part of the shared gate.
    return !this.readOnlyItems() && canMutateField(this.field);
  }
}
